import type { ReactNode } from 'react';
import { cx } from '../cx';
import { LABEL_TEXT, TICK_TEXT, VALUE_TEXT, niceScale, percentOf, thinTick } from './scale';
import { WIDE_ONLY, type ChartLayout } from './layout';
import { SampleCount, sampleTier, type ThinLabels } from './thin';

export type SwarmRow = {
  id: string;
  label: ReactNode;
  /** One value per ad (monthly pay, days open). */
  values: number[];
};

export type JobSwarmProps = {
  rows: SwarmRow[];
  /** The job the page is about: drawn in sun with an ink ring in its row, its value written. */
  thisJob?: { rowId: string; value: number; label: ReactNode };
  formatTick: (value: number) => string;
  thinLabels: ThinLabels;
  /** `auto` (default) switches to the narrow form in a narrow container: row names above each row, smaller dots. */
  layout?: ChartLayout;
  /** One sentence with each row's median and this job's value, for screen readers. */
  description: string;
  className?: string;
};

export const medianOf = (values: number[]): number => {
  const s = [...values].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/**
 * Stack dots that would overlap into levels above and below the row's centre line (0, -1, 1, -2,
 * 2, ...), in percent of the plot's width. Returns each dot's level, capped at `maxLevel`.
 */
export function swarmLevels(positions: number[], gap = 1.6, maxLevel = 3): number[] {
  const order = positions.map((p, i) => ({ p, i })).sort((a, b) => a.p - b.p);
  const placed: Array<{ p: number; level: number }> = [];
  const out = positions.map(() => 0);
  const candidates = [0];
  for (let k = 1; k <= maxLevel; k++) candidates.push(-k, k);
  for (const { p, i } of order) {
    const level =
      candidates.find((l) => !placed.some((d) => d.level === l && Math.abs(d.p - p) < gap)) ??
      candidates[candidates.length - 1];
    placed.push({ p, level });
    out[i] = level;
  }
  return out;
}

/**
 * One dot per ad on a shared scale, one row per group, packed so dots do not overlap, with each
 * row's median as an ink tick and its n. Shows where one job sits among comparable ads better than
 * a box plot, which most readers cannot read. Rows with 10 to 19 ads keep their dots but their
 * median is marked rough; rows under 10 say "too few ads". The table twin links each ad.
 */
const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col gap-0.5 @md:flex-row @md:items-center @md:gap-3',
  narrow: 'flex flex-col gap-0.5',
  wide: 'flex items-center gap-3',
};
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: 'flex items-baseline gap-2 @md:w-1/4 @md:shrink-0 @md:flex-col @md:items-start @md:gap-0',
  narrow: 'flex items-baseline gap-2',
  wide: 'flex w-1/4 shrink-0 flex-col',
};
// In a column, flex-1 would collapse the plot to no height: the narrow form sizes it by width.
const PLOT: Record<ChartLayout, string> = { auto: 'w-full @md:w-auto @md:flex-1', narrow: 'w-full', wide: 'flex-1' };
const DOT: Record<ChartLayout, string> = { auto: 'size-1.5 @md:size-2', narrow: 'size-1.5', wide: 'size-2' };

export function JobSwarm({
  rows,
  thisJob,
  formatTick,
  thinLabels,
  layout = 'auto',
  description,
  className,
}: JobSwarmProps) {
  const shown = rows.filter((r) => sampleTier(r.values.length) !== 'none');
  const all = [...shown.flatMap((r) => r.values), ...(thisJob ? [thisJob.value] : [])];
  const scale = niceScale(Math.min(...all), Math.max(...all));
  const pos = (v: number) => percentOf(v, scale.min, scale.max);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      {rows.map((r) => {
        const tier = sampleTier(r.values.length);
        const levels = swarmLevels(r.values.map(pos), layout === 'wide' ? 1.6 : 2.2);
        const m = tier === 'none' ? 0 : medianOf(r.values);
        const job = thisJob?.rowId === r.id ? thisJob : undefined;
        return (
          <div key={r.id} className={ROW[layout]}>
            <span className={ROW_LABEL[layout]}>
              <span className={cx('truncate', LABEL_TEXT)}>{r.label}</span>
              <SampleCount n={r.values.length} />
            </span>
            <div className={cx('relative mr-4', PLOT[layout], job ? 'h-24' : 'h-16')}>
              {tier === 'none' ? (
                <span className="absolute top-1/2 left-0 -translate-y-1/2 text-xs text-jc-ink-secondary">
                  {thinLabels.tooFew}
                </span>
              ) : (
                <>
                  {r.values.map((v, k) => (
                    <span
                      key={k}
                      aria-hidden="true"
                      className={cx('absolute -translate-1/2 rounded-full bg-jc-chart-context', DOT[layout])}
                      style={{ left: `${pos(v)}%`, top: `calc(50% + ${levels[k] * 0.5}rem)` }}
                    />
                  ))}
                  <span
                    aria-hidden="true"
                    className={cx(
                      'absolute inset-y-1 -translate-x-1/2 rounded-full',
                      tier === 'full' ? 'w-1 bg-jc-ink' : 'border-l-2 border-dashed border-jc-ink',
                    )}
                    style={{ left: `${pos(m)}%` }}
                  />
                  {tier === 'rough' ? (
                    <span className="absolute top-0 pl-2 text-xs text-jc-ink-secondary" style={{ left: `${pos(m)}%` }}>
                      {thinLabels.rough}
                    </span>
                  ) : null}
                  {job ? (
                    <>
                      <span
                        aria-hidden="true"
                        className="absolute top-1/2 z-10 size-3.5 -translate-1/2 rounded-full bg-jc-chart-2 ring-2 ring-jc-ink"
                        style={{ left: `${pos(job.value)}%` }}
                      />
                      {/* Above the swarm, so it never sits on other dots. */}
                      <span
                        aria-hidden="true"
                        className={cx(
                          'absolute top-0 whitespace-nowrap',
                          VALUE_TEXT,
                          pos(job.value) > 60 ? '-translate-x-full' : '-translate-x-1/4',
                        )}
                        style={{ left: `${pos(job.value)}%` }}
                      >
                        {job.label}
                      </span>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
        );
      })}
      <div className="flex gap-3" aria-hidden="true">
        <span className={cx('w-1/4 shrink-0', WIDE_ONLY[layout])} />
        <div className="relative mr-4 h-4 flex-1">
          {scale.ticks.map((t, i) => (
            <span
              key={t}
              className={cx('absolute -translate-x-1/2 whitespace-nowrap', TICK_TEXT, thinTick(i, scale.ticks.length))}
              style={{ left: `${pos(t)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
